import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import cloneDeep from 'lodash.clonedeep'
import { useRouter } from 'next/router'
import { plainToInstance } from 'class-transformer'

import { useCategories } from '@hooks/useCategories'
import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { MenuTypeEnum } from '@interfaces/MenuTypeEnum'
import { PrimaryTypeEnum } from '@interfaces/PrimaryTypeEnum'
import { OrderedKeyEnum } from '@interfaces/OrderedEnum'
import { SortEnum } from '@interfaces/SortEnum'
import { queryClient } from '@client/init'
import { AdvancedSearchFormType } from '@features/allCategory/components/AdvancedSearchForm/interface'
import { SearchRecommendType } from '@models/recommend/SearchRecommendType'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { scrollToElement } from '@lib/utils'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { useBookAction } from '@hooks/book/useBookAction'
import { useCategoryAction } from '@hooks/category/useCategoryAction'
import { SearchRecommendationResponse } from '@models/recommend/SearchRecommendationResponse'
import {
  AllCategoryPageProps,
  AllCategorySearchType,
  WithAllCategoryPageProps,
} from './interface'

export function withAllCategoryPage(Component: React.FC<AllCategoryPageProps>) {
  function WithAllCategoryPage({
    bookType,
    title,
    categoryDetail,
    categoryId,
    primaryType,
    queryOrderBy,
    querySecondaryType,
    advanceSearch,
  }: WithAllCategoryPageProps) {
    const categoryClient = useCategoryAction()
    const router = useRouter()
    const { isAuthenticated, canFetchApi } = useAuthentication()
    const bookClient = useBookAction()
    const storyDrawer = useDrawer({ drawer: 'story' })
    const { novelCategories, mangaCategories } = useCategories()
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const loginModal = useModal({ modal: 'login' })
    const [allCategorySearchParam, setAllCategorySearchParam] =
      useState<AllCategorySearchType>({
        categoryId: '',
        primaryType: PrimaryTypeEnum.TOP_CHART,
        orderBy: `${OrderedKeyEnum.POPULAR}:${SortEnum.DESC}`,
        secondaryType: undefined,
      })
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(advanceSearch)

    const categories = useMemo(() => {
      const data =
        bookType === MenuTypeEnum.MANGA ? mangaCategories : novelCategories
      return [
        { text: 'ทุกหมวดหมู่', value: '' },
        ...data.map(row => ({
          text: row.label,
          value: row.value,
        })),
      ]
    }, [bookType, novelCategories, mangaCategories])

    const queryKey = [
      'all-categories',
      bookType,
      allCategorySearchParam,
      page,
      perpage,
    ]
    const { data: allCategory, isLoading } = useQuery(
      queryKey,
      () =>
        categoryClient.getCategoryList({
          allCategorySearchParam,
          page,
          perpage,
          bookType,
        }),
      {
        enabled: canFetchApi,
      }
    )

    async function handleCategoryChange(value: string) {
      await router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          categoryId: value,
        },
      })
      if (page !== 1) {
        pageChange(1)
      }
    }

    function getOrderBy(value: PrimaryTypeEnum) {
      let orderBy = `${OrderedKeyEnum.CREATED_AT}:${SortEnum.DESC}`

      if (value === PrimaryTypeEnum.HOT_NEW) {
        orderBy = `${OrderedKeyEnum.POPULAR}:${SortEnum.DESC}`
      }

      if ([PrimaryTypeEnum.NEW, PrimaryTypeEnum.ENDED].includes(value)) {
        orderBy = `${OrderedKeyEnum.UPDATED_AT}:${SortEnum.DESC}`
      }

      if (value === PrimaryTypeEnum.TOP_CHART) {
        orderBy = `${OrderedKeyEnum.PURCHASED}:${SortEnum.DESC}`
      }

      return orderBy
    }

    async function handlePrimaryTypeChange(value: PrimaryTypeEnum) {
      const orderBy = getOrderBy(value)
      await router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          primaryType: value,
          orderBy,
          secondaryType: undefined,
        },
      })

      if (page !== 1) {
        pageChange(1)
      }
    }

    async function handleClickAdvancedSearchButton() {
      if (showAdvancedSearch) {
        const orderBy = getOrderBy(allCategorySearchParam.primaryType)
        await router.replace({
          pathname: router.pathname,
          query: {
            ...router.query,
            orderBy,
            secondaryType: undefined,
            advanceSearch: !showAdvancedSearch,
          },
        })
      } else {
        await router.replace({
          pathname: router.pathname,
          query: {
            ...router.query,
            advanceSearch: !showAdvancedSearch,
          },
        })
      }
    }

    const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
      useMutation((id: number) => bookClient.toggleBookLike(id), {
        onSuccess: (type, id) => {
          queryClient.setQueryData<SearchRecommendationResponse | undefined>(
            queryKey,
            (oldData?: SearchRecommendationResponse) => {
              if (oldData) {
                const temp = cloneDeep(oldData)
                const index = temp.data.findIndex(row => row.id === id)

                if (index !== -1) {
                  temp.data[index].isLiked =
                    type === LikeSuccessStatusEnum.CREATE
                  temp.data[index].likeCount =
                    type === LikeSuccessStatusEnum.CREATE
                      ? temp.data[index].likeCount + 1
                      : Math.max(0, temp.data[index].likeCount - 1)
                }

                return temp
              }

              return oldData
            }
          )
        },
      })

    async function handleLikeBook(id: number) {
      if (isAuthenticated) {
        await toggleBookLike(id)
      } else {
        loginModal.show()
      }
    }

    function handlePageChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#all-category-list')
    }

    function openStoryDrawer(value: SearchRecommendType) {
      storyDrawer.show({
        bookId: value.id,
        initialData: plainToInstance(StoryDetailReaderType, {
          id: value.id,
          coverImgPath: value.coverImgPath,
          title: value.title,
          writingType: value.writingType,
          publishedAt: value.publishedAt,
          cover: value.cover,
          viewCount: value.viewCount,
          commentCount: value.commentCount,
          likeCount: value.likeCount,
          publishedChapterCount: value.chapterCount,
          penName: value.penName,
          category: value.category,
          bookType: value.bookType,
          tags: value.tags,
          rating: {
            name: '',
          },
          isTranslated: value.isTranslated,
          isEnded: value.isEnded,
          isEpub: value.isEpub,
          isPdf: value.isPdf,
          isLiked: value.isLiked,
        }),
      })
    }

    async function handleSubmit({
      secondaryType,
      orderBy,
    }: AdvancedSearchFormType) {
      await router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          secondaryType,
          orderBy,
        },
      })
      if (page !== 1) {
        pageChange(1)
      }
    }

    useEffect(() => {
      setAllCategorySearchParam({
        categoryId,
        primaryType,
        orderBy: queryOrderBy,
        secondaryType: querySecondaryType || undefined,
      })
      setShowAdvancedSearch(advanceSearch)
    }, [
      categoryId,
      primaryType,
      queryOrderBy,
      querySecondaryType,
      advanceSearch,
    ])

    const componentProps = {
      data: allCategory?.data || [],
      title,
      categories,
      page,
      perpage,
      total: allCategory?.total || 0,
      allCategorySearchParam,
      categoryDetail,
      showAdvancedSearch,
      isLoading,
      isLoadingToggleBookLike,
      handleCategoryChange,
      handlePrimaryTypeChange,
      handleClickAdvancedSearchButton,
      getOrderBy,
      perpageChange,
      handleLikeBook,
      handlePageChange,
      openStoryDrawer,
      handleSubmit,
    }

    return <Component {...componentProps} />
  }
  return WithAllCategoryPage
}
